import type { MentionAddedType } from '@/components/composite/CaptionComponent/types/MentionType';

import { FACEBOOK, INSTAGRAM, LINKEDIN, THREADS, TIKTOK_BUSINESS, X } from '@/lib/socialProfile';

import type { SocialProfileSchedulerType } from '@/components/composite/Scheduler/Type/SocialProfileSchedulerType';

const HASHTAG = 'HASHTAG';
const MENTION = 'MENTION';

const getHashtagMentionLink = (
    network: SocialProfileSchedulerType['social_network_type'],
    type: typeof HASHTAG | typeof MENTION
) => {
    switch (network) {
        case INSTAGRAM:
            if (type === HASHTAG) {
                return 'https://www.instagram.com/explore/tags/';
            }
            return 'https://www.instagram.com/';
        case FACEBOOK:
            if (type === HASHTAG) {
                return 'https://www.facebook.com/hashtag/';
            }
            return 'https://www.facebook.com/';
        case X:
            if (type === HASHTAG) {
                return 'https://x.com/hashtag/';
            }
            return 'https://x.com/';
        case LINKEDIN:
            if (type === HASHTAG) {
                return 'https://www.linkedin.com/feed/hashtag/?keywords=';
            }
            return 'https://www.linkedin.com/company/';
        case TIKTOK_BUSINESS:
            if (type === HASHTAG) {
                return 'https://www.tiktok.com/search?q=';
            }
            return 'https://www.tiktok.com/@';
        case THREADS:
            if (type === HASHTAG) {
                return 'https://www.threads.com/hashtag/';
            }
            return 'https://www.threads.com/';
    }
};

type OffsetResultType = {
    type: typeof MENTION | typeof HASHTAG;
    name: string;
    offset: number;
    length: number;
    link: string;
};

type GetMentionOffsetType = {
    mentions: MentionAddedType[];
    name: string;
    match: RegExpMatchArray;
    index_string: number;
    offset: number;
    length: number;
};

function getFacebookMentionOffset({
    mentions,
    name,
    match,
    index_string,
    offset,
    length
}: GetMentionOffsetType): OffsetResultType {
    const id = match?.[2]; // 12345
    const mention = mentions?.filter((mention) => mention?.id === id)?.[0];
    if (!mention) {
        return;
    }

    return {
        type: MENTION,
        name, // @mention | @[id][mention]
        offset: index_string + offset,
        length,
        link: `${getHashtagMentionLink(FACEBOOK, MENTION)}${mention?.display?.replaceAll(
            ' ',
            '-'
        )}-${mention.id}`
    };
}

function getLinkedinMentionOffset({
    name,
    match,
    index_string,
    offset,
    length
}: Omit<GetMentionOffsetType, 'mentions'>): OffsetResultType {
    const splitOrganisationId = match[4]?.split(':');
    const id = splitOrganisationId?.[splitOrganisationId?.length - 1];
    if (!id) {
        return;
    }
    return {
        type: MENTION,
        name,
        offset: index_string + offset,
        length,
        link: `${getHashtagMentionLink(LINKEDIN, MENTION)}${id}`
    };
}

export function getOffsets(
    caption: string,
    reg: RegExp,
    mentions: MentionAddedType[],
    network: SocialProfileSchedulerType['social_network_type']
) {
    const results: OffsetResultType[] = [];

    let index_string = 0;
    let tmp = caption.substring(index_string);
    let match = tmp.match(reg);

    while (match && match.length) {
        const name = match[0]; // @mention | #hashtag | @[id][mention]
        const type = name?.startsWith('#') ? HASHTAG : MENTION;
        const offset = match?.index ?? undefined;
        const length = name?.length;

        if (type === MENTION && network === FACEBOOK) {
            const result = getFacebookMentionOffset({
                mentions,
                name,
                match,
                index_string,
                offset,
                length
            });
            if (result) {
                results.push(result);
            }
        }
        if (type === MENTION && network === LINKEDIN) {
            const result = getLinkedinMentionOffset({ name, match, index_string, offset, length });
            if (result) {
                results.push(result);
            }
        }
        if (type === MENTION && network !== LINKEDIN && network !== FACEBOOK) {
            const cleanName = name.trimStart();
            const leadingWhitespace = name.length - cleanName.length; // We need to add the leading whitespace to the offset to get the correct offset
            results.push({
                type,
                name: cleanName,
                offset: index_string + offset + leadingWhitespace,
                length: cleanName.length,
                link: `${getHashtagMentionLink(network, type)}${cleanName.trim().replace('@', '')}`
            });
        }
        if (type === HASHTAG) {
            results.push({
                type,
                name, // #hashtag
                offset: index_string + offset,
                length,
                link: `${getHashtagMentionLink(network, type)}${name.trim().replace('#', '')}`
            });
        }

        index_string += offset + length;

        tmp = caption.substring(index_string);
        match = tmp.match(reg);
    }

    return results;
}

export function sanitizeFacebook(prepared_caption: string) {
    const regex = /(@\[([^\]]+)\]\[([^\]]+)\])/mu;

    let match = prepared_caption.match(regex);
    while (match && match.length) {
        prepared_caption = prepared_caption.replace(regex, match[3]);
        match = prepared_caption.match(regex);
    }

    return prepared_caption?.replaceAll(new RegExp(/(<br\ ?\/?>){3,}/, 'g'), '<br/><br/>');
}

export function sanitizeLinkedin(prepared_caption: string) {
    const regex = /(@\[([^\]]+)\]\(([^)]+)\))/mu;

    let match = prepared_caption.match(regex);
    while (match && match.length) {
        prepared_caption = prepared_caption.replace(regex, '$2');
        match = prepared_caption.match(regex);
    }

    return prepared_caption?.replaceAll(new RegExp(/(<br\ ?\/?>){3,}/, 'g'), '<br/><br/>');
}
